import { Controller } from "@hotwired/stimulus"

// Combobox multi-select for the Specification/Drawings field.
// The input is directly typeable — typing filters the dropdown.
// Selecting items from the dropdown adds chips below the input.
// Free text typed without selecting from dropdown is also saved.

export default class extends Controller {
  static targets = [
    "input", "dropdown", "list",
    "chips", "hidden",
    "browser", "browserSearch", "browserList", "browserCount", "browserFilters", "browserContentStatus",
    "browserPreviewTitle", "browserPreviewEmpty", "browserPreviewLoading", "browserPreviewFrame"
  ]

  static values = {
    sheetsUrl: String,
    sectionsUrl: String,
    devPdfsUrl: String,
    sheetsContentUrl: String,
    designContentUrl: String,
    sectionsContentUrl: String,
    suggestUrl: String,
    selected: { type: String, default: "" }
  }

  connect() {
    this._options = []
    this._selected = [] // items picked from dropdown
    this._loaded = false
    const initial = (this.selectedValue || "").trim()
    if (initial) {
      initial.split(",").map(s => s.trim()).filter(Boolean).forEach(val => {
        this._selected.push({ id: val, label: val })
      })
    }

    this._renderChips()
    this._syncHidden()

    this._onClickOutside = (e) => {
      if (!this.element.contains(e.target)) this._close()
    }
    document.addEventListener("click", this._onClickOutside)
  }

  disconnect() {
    document.removeEventListener("click", this._onClickOutside)
  }

  // Restore picks from a saved draft ([{id, label}]) — same state the
  // dropdown/browser write, so chips + the hidden field follow.
  setSelection(items) {
    this._selected = (items || []).filter(i => i && i.id).map(i => ({ id: String(i.id), label: i.label || String(i.id) }))
    this._renderChips()
    this._syncHidden()
  }

  // ── Combobox interactions ───────────────────────────────────────

  async onFocus() {
    if (!this._loaded) await this._fetchOptions()
    this._renderList(this.inputTarget.value.trim().toLowerCase())
  }

  onInput() {
    const q = this.inputTarget.value.trim().toLowerCase()
    this._renderList(q)
    this._syncHidden()
  }

  onKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this._close()
    }
  }

  _open() {
    if (this.hasDropdownTarget) this.dropdownTarget.classList.remove("hidden")
  }

  _close() {
    if (this.hasDropdownTarget) this.dropdownTarget.classList.add("hidden")
  }

  // ── Select / deselect ──────────────────────────────────────────

  selectOption(event) {
    const { id, label } = event.currentTarget.dataset
    const idx = this._selected.findIndex(s => s.id === id)
    if (idx >= 0) {
      this._selected.splice(idx, 1)
    } else {
      this._selected.push({ id, label })
    }
    this._renderChips()
    this._syncHidden()
    // Re-render list to update checkmarks
    const q = this.inputTarget.value.trim().toLowerCase()
    this._renderList(q)
    // Keep focus on input
    this.inputTarget.focus()
  }

  removeChip(event) {
    const id = event.currentTarget.dataset.id
    this._selected = this._selected.filter(s => s.id !== id)
    this._renderChips()
    this._syncHidden()
    // Update list if dropdown is open
    if (this.hasDropdownTarget && !this.dropdownTarget.classList.contains("hidden")) {
      const q = this.inputTarget.value.trim().toLowerCase()
      this._renderList(q)
    }
  }

  // ── Browse pop-up (Kloepfer item 10) ───────────────────────────
  // A roomy, grouped, searchable list of every drawing & spec — easier to
  // find a sheet than by name in the small combobox dropdown. Reuses the
  // same _selected state, so chips + the hidden field update live.

  async openBrowser() {
    if (!this.hasBrowserTarget) return
    if (!this._loaded) await this._fetchOptions()
    this._close() // hide the inline combobox dropdown if open
    this._resetPreview()
    this._setGroupFilter("")
    this.browserTarget.classList.remove("hidden")
    if (this.hasBrowserSearchTarget) {
      this.browserSearchTarget.value = ""
      this.browserSearchTarget.focus()
    }
    this._renderBrowser("")
  }

  // All / Drawings / Specs pills above the search (Phelipe 2026-07-22).
  browserGroupFilter(event) {
    this._setGroupFilter(event.currentTarget.dataset.filter || "")
    this._renderBrowser(this.hasBrowserSearchTarget ? this.browserSearchTarget.value.trim().toLowerCase() : "")
  }

  _setGroupFilter(value) {
    this._browserGroupFilter = value
    if (!this.hasBrowserFiltersTarget) return
    this.browserFiltersTarget.querySelectorAll("[data-filter]").forEach(btn => {
      const active = (btn.dataset.filter || "") === value
      btn.classList.toggle("bg-brand", active)
      btn.classList.toggle("text-white", active)
      btn.classList.toggle("bg-gray-100", !active)
      btn.classList.toggle("text-gray-600", !active)
      btn.classList.toggle("hover:bg-gray-200", !active)
    })
  }

  closeBrowser() {
    if (this.hasBrowserTarget) this.browserTarget.classList.add("hidden")
    this._resetPreview()
  }

  browserBackdrop(event) {
    if (event.target === this.browserTarget) this.closeBrowser()
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  browserFilter() {
    const query = this.browserSearchTarget.value.trim().toLowerCase()
    this._renderBrowser(query)
    this._queueContentSearch(query)
  }

  // ── Search inside the files (Phelipe 2026-07-22) ───────────────
  // Rides the same DocumentContentSearch index as the Drawings/Specs list
  // search: matching sheet/section numbers come back and their rows join
  // the results with an "in file text" badge.

  _queueContentSearch(query) {
    clearTimeout(this._contentSearchTimer)
    const hasUrls = this.sheetsContentUrlValue || this.sectionsContentUrlValue || this.designContentUrlValue
    if (!hasUrls || query.length < 3) {
      this._contentMatches = null
      this._setContentStatus("")
      if (!query) this._renderBrowser(query)
      return
    }

    this._setContentStatus("Searching inside the files\u2026")
    this._contentSearchTimer = setTimeout(async () => {
      try {
        const fetches = []
        for (const url of [this.sheetsContentUrlValue, this.sectionsContentUrlValue, this.designContentUrlValue]) {
          if (url) fetches.push(fetch(`${url}?q=${encodeURIComponent(query)}`, { credentials: "same-origin" })
            .then(r => r.json()).catch(() => ({ ids: [] })))
        }
        const results = await Promise.all(fetches)
        // A newer search superseded this one — drop the stale answer.
        const current = this.hasBrowserSearchTarget ? this.browserSearchTarget.value.trim().toLowerCase() : ""
        if (current !== query) return

        const ids = new Set()
        results.forEach(r => (r.ids || []).forEach(id => ids.add(String(id))))
        this._contentMatches = { query, ids }
        this._setContentStatus(ids.size > 0
          ? `${ids.size} file${ids.size === 1 ? "" : "s"} mention it inside`
          : "No mentions inside the files")
        this._renderBrowser(query)
      } catch {
        this._setContentStatus("")
      }
    }, 350)
  }

  _setContentStatus(message) {
    if (!this.hasBrowserContentStatusTarget) return
    this.browserContentStatusTarget.textContent = message || ""
    this.browserContentStatusTarget.classList.toggle("hidden", !message)
  }

  // ── AI suggestions tab (Phelipe 2026-07-22) ────────────────────
  // Gemini reads the in-progress action item (title, type, description,
  // attached file names) and picks relevant documents from the catalog.
  // Results cache per context digest, so flipping tabs is free until the
  // item's text changes.

  _suggestContext() {
    const root = this.element.closest('[data-controller~="action-item-form"]') || document
    const title = root.querySelector('[data-action-item-form-target="titleInput"]')?.value?.trim() || ""
    const description = root.querySelector('[data-action-item-form-target="descriptionInput"]')?.value?.trim() || ""
    const kindSelect = root.querySelector('[data-action-item-form-target="kindSelect"]')
    const kind = kindSelect ? (kindSelect.selectedOptions[0]?.textContent || "").trim() : ""
    const filenames = Array.from(root.querySelectorAll(
      '[data-action-item-form-target="fileList"] span[title], [data-action-item-form-target="photoList"] span[title]'
    )).map(el => el.getAttribute("title")).filter(Boolean).slice(0, 10)
    return { title, description, kind, filenames }
  }

  _renderAiSuggestions(query) {
    this._updateBrowserCount()
    const ctx = this._suggestContext()
    if (!ctx.title && !ctx.description) {
      this.browserListTarget.innerHTML = `<div class="px-4 py-10 text-sm text-gray-400 text-center">Give the action item a title or a description first — ConsLog AI reads them (and any attached files' names) to suggest documents.</div>`
      return
    }

    const digest = JSON.stringify([ctx.title, ctx.kind, ctx.description, ctx.filenames])
    if (this._aiSuggestions && this._aiSuggestions.digest === digest) {
      this._renderAiSuggestionRows(query)
      return
    }

    this.browserListTarget.innerHTML = `<div class="px-4 py-12 text-center text-sm"><span class="font-bold ai-rainbow-text">ConsLog AI — reading the action item\u2026</span></div>`
    this._fetchAiSuggestions(digest, ctx)
  }

  async _fetchAiSuggestions(digest, ctx) {
    if (this._aiSuggestInFlight === digest) return
    this._aiSuggestInFlight = digest
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch(this.suggestUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify(ctx)
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || "AI couldn't run")

      const byId = new Map(this._options.map(o => [String(o.id), o]))
      const items = (data.suggestions || []).filter(s => byId.has(String(s.id)))
        .map(s => ({ option: byId.get(String(s.id)), reason: s.reason || "" }))
      this._aiSuggestions = { digest, items }
      if (this._browserGroupFilter === "ai") this._renderAiSuggestionRows(this._currentBrowserQuery())
    } catch (err) {
      this._aiSuggestions = null
      if (this._browserGroupFilter === "ai") {
        this.browserListTarget.innerHTML = `<div class="px-4 py-10 text-sm text-gray-400 text-center">${this._esc(err.message || "ConsLog AI couldn't run — try again.")}</div>`
      }
    } finally {
      this._aiSuggestInFlight = null
    }
  }

  _currentBrowserQuery() {
    return this.hasBrowserSearchTarget ? this.browserSearchTarget.value.trim().toLowerCase() : ""
  }

  _renderAiSuggestionRows(query) {
    const items = (this._aiSuggestions?.items || []).filter(({ option }) =>
      !query || option.label.toLowerCase().includes(query) || String(option.id).toLowerCase().includes(query))
    if (items.length === 0) {
      this.browserListTarget.innerHTML = `<div class="px-4 py-10 text-sm text-gray-400 text-center">${query ? "No suggestions match the search" : "No confident matches — try the All tab or add more detail to the item."}</div>`
      return
    }
    const selectedIds = new Set(this._selected.map(s => s.id))
    this.browserListTarget.innerHTML =
      `<div class="px-3 pt-3 pb-1 text-[11px] font-bold text-gray-400 uppercase tracking-wider">Suggested for this action item</div>` +
      items.map(({ option, reason }) =>
        this._browserRowHtml(option, selectedIds.has(option.id)) +
        (reason ? `<div class="pl-11 pr-3 -mt-1.5 pb-2 text-[12px] text-purple-700">${this._esc(reason)}</div>` : "")
      ).join("")
  }

  _renderBrowser(query) {
    if (!this.hasBrowserListTarget) return

    // AI suggestions tab (Phelipe 2026-07-22) takes over the list pane.
    if (this._browserGroupFilter === "ai") {
      this._renderAiSuggestions(query)
      return
    }

    const selectedIds = new Set(this._selected.map(s => s.id))
    const content = (this._contentMatches && this._contentMatches.query === query) ? this._contentMatches.ids : null
    const labelHit = (o) => !query || o.label.toLowerCase().includes(query) || String(o.id).toLowerCase().includes(query)
    const filterFn = (o) => labelHit(o) || (content && content.has(String(o.id)))
    const groupFilter = this._browserGroupFilter || ""
    const groups = [
      { key: "design_pdfs", label: "Design PDFs" },
      { key: "drawings", label: "Drawings" },
      { key: "specs", label: "Specifications" }
    ].filter(g => !groupFilter || g.key === groupFilter)

    let html = ""
    for (const g of groups) {
      const items = this._options.filter(o => o.group === g.key).filter(filterFn)
      if (items.length === 0) continue
      html += `<div class="px-3 pt-3 pb-1 text-[11px] font-bold text-gray-400 uppercase tracking-wider">${g.label}</div>`
      html += items.map(o => this._browserRowHtml(o, selectedIds.has(o.id), !!(query && content && content.has(String(o.id)) && !labelHit(o)))).join("")
    }

    if (html === "") {
      html = `<div class="px-3 py-10 text-sm text-gray-400 text-center">${query ? "No matches" : "Nothing here yet"}</div>`
    }
    this.browserListTarget.innerHTML = html
    this._updateBrowserCount()
  }

  // Row click previews the actual PDF (item 18); the checkbox toggles the
  // selection. isActive = the row whose document is on screen.
  _browserRowHtml(option, isSelected, contentOnly = false) {
    const isActive = this._activePreviewId === option.id
    const check = isSelected
      ? `<svg class="w-5 h-5 text-brand shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>`
      : `<div class="w-5 h-5 shrink-0 rounded border border-gray-300"></div>`
    return `<div data-action="click->spec-drawings-select#browserPreview"
                 data-id="${this._esc(option.id)}"
                 data-label="${this._esc(option.label)}"
                 data-preview-url="${this._esc(option.previewUrl || "")}"
                 class="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-colors hover:bg-gray-50 ${isActive ? "bg-gray-100" : (isSelected ? "bg-brand/5" : "")}">
      <span data-action="click->spec-drawings-select#browserToggle"
            data-id="${this._esc(option.id)}" data-label="${this._esc(option.label)}"
            class="shrink-0 p-1 -m-1 rounded hover:bg-brand/10">${check}</span>
      <span class="text-sm flex-1 min-w-0 truncate ${isSelected ? "text-brand font-medium" : "text-gray-700"}">${this._esc(option.label)}</span>
      ${contentOnly ? '<span class="shrink-0 text-[11px] font-medium text-purple-700 bg-purple-50 rounded px-1.5 py-0.5">in file text</span>' : ""}
    </div>`
  }

  // Toggle from the pop-up: same as selectOption, but re-render the pop-up
  // list (and keep the inline chips/hidden field in sync). Stops the click
  // so it doesn't also fire the row's preview.
  browserToggle(event) {
    event.stopPropagation()
    const { id, label } = event.currentTarget.dataset
    const idx = this._selected.findIndex(s => s.id === id)
    if (idx >= 0) this._selected.splice(idx, 1)
    else this._selected.push({ id, label })

    this._renderChips()
    this._syncHidden()
    this._renderBrowser(this.hasBrowserSearchTarget ? this.browserSearchTarget.value.trim().toLowerCase() : "")
  }

  // ── PDF preview pane (item 18) ─────────────────────────────────
  // Fetch the presigned file as a blob and hand the browser's built-in PDF
  // viewer a blob: URL — dodges S3 content-disposition and cross-origin
  // iframe quirks (same trick the Apryse loaders use).

  async browserPreview(event) {
    const { id, label, previewUrl } = event.currentTarget.dataset
    this._activePreviewId = id
    this._renderBrowser(this.hasBrowserSearchTarget ? this.browserSearchTarget.value.trim().toLowerCase() : "")

    if (this.hasBrowserPreviewTitleTarget) {
      this.browserPreviewTitleTarget.textContent = label
      this.browserPreviewTitleTarget.classList.remove("hidden")
    }
    if (!previewUrl) { this._showPreviewState("empty"); return }

    this._showPreviewState("loading")
    try {
      const resp = await fetch(previewUrl)
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      const blob = new Blob([await resp.arrayBuffer()], { type: "application/pdf" })
      if (this._activePreviewId !== id) return // a newer click won
      if (this._previewObjectUrl) URL.revokeObjectURL(this._previewObjectUrl)
      this._previewObjectUrl = URL.createObjectURL(blob)
      this.browserPreviewFrameTarget.src = this._previewObjectUrl
      this._showPreviewState("frame")
    } catch {
      if (this._activePreviewId === id) this._showPreviewState("empty")
    }
  }

  _showPreviewState(state) {
    if (this.hasBrowserPreviewEmptyTarget) this.browserPreviewEmptyTarget.classList.toggle("hidden", state !== "empty")
    if (this.hasBrowserPreviewLoadingTarget) this.browserPreviewLoadingTarget.classList.toggle("hidden", state !== "loading")
    if (this.hasBrowserPreviewFrameTarget) this.browserPreviewFrameTarget.classList.toggle("hidden", state !== "frame")
  }

  _resetPreview() {
    this._activePreviewId = null
    if (this._previewObjectUrl) { URL.revokeObjectURL(this._previewObjectUrl); this._previewObjectUrl = null }
    if (this.hasBrowserPreviewFrameTarget) this.browserPreviewFrameTarget.removeAttribute("src")
    if (this.hasBrowserPreviewTitleTarget) this.browserPreviewTitleTarget.classList.add("hidden")
    this._showPreviewState("empty")
  }

  _updateBrowserCount() {
    if (!this.hasBrowserCountTarget) return
    const n = this._selected.length
    this.browserCountTarget.textContent = n === 0 ? "None selected" : `${n} selected`
  }

  // ── Private: Fetch options ─────────────────────────────────────

  async _fetchOptions() {
    this._options = []

    const fetches = []
    if (this.sheetsUrlValue) {
      fetches.push(
        fetch(this.sheetsUrlValue, { credentials: "same-origin" })
          .then(r => r.json())
          .then(items => items.forEach(i => this._options.push({ id: i.id, label: i.label, group: "drawings", previewUrl: i.preview_url })))
          .catch(() => {})
      )
    }
    if (this.sectionsUrlValue) {
      fetches.push(
        fetch(this.sectionsUrlValue, { credentials: "same-origin" })
          .then(r => r.json())
          .then(items => items.forEach(i => this._options.push({ id: i.id, label: i.label, group: "specs", previewUrl: i.preview_url })))
          .catch(() => {})
      )
    }
    if (this.devPdfsUrlValue) {
      fetches.push(
        fetch(this.devPdfsUrlValue, { credentials: "same-origin" })
          .then(r => r.json())
          .then(items => items.forEach(i => this._options.push({ id: i.id, label: i.label, group: "design_pdfs", previewUrl: i.preview_url })))
          .catch(() => {})
      )
    }

    await Promise.all(fetches)
    this._loaded = true

    for (const sel of this._selected) {
      const match = this._options.find(o => o.id === sel.id)
      if (match) sel.label = match.label
    }
    this._renderChips()
  }

  // ── Private: Render list ───────────────────────────────────────

  _renderList(query) {
    if (!this.hasListTarget) return

    const selectedIds = new Set(this._selected.map(s => s.id))
    const drawings = this._options.filter(o => o.group === "drawings")
    const specs = this._options.filter(o => o.group === "specs")
    const designPdfs = this._options.filter(o => o.group === "design_pdfs")

    const filterFn = (o) => !query || o.label.toLowerCase().includes(query) || o.id.toLowerCase().includes(query)
    const filteredDrawings = drawings.filter(filterFn)
    const filteredSpecs = specs.filter(filterFn)
    const filteredDesignPdfs = designPdfs.filter(filterFn)

    if (filteredDrawings.length === 0 && filteredSpecs.length === 0 && filteredDesignPdfs.length === 0) {
      this._close()
      return
    }

    this._open()
    let html = ""
    let prevGroup = false

    if (filteredDesignPdfs.length > 0) {
      html += `<div class="px-3 py-1.5 text-[11px] font-bold text-gray-400 uppercase tracking-wider bg-gray-50 border-b border-gray-100 sticky top-0">Design PDFs</div>`
      html += filteredDesignPdfs.map(o => this._optionHtml(o, selectedIds.has(o.id))).join("")
      prevGroup = true
    }

    if (filteredDrawings.length > 0) {
      html += `<div class="px-3 py-1.5 text-[11px] font-bold text-gray-400 uppercase tracking-wider bg-gray-50 border-b border-gray-100 ${prevGroup ? "border-t" : ""} sticky top-0">Drawings</div>`
      html += filteredDrawings.map(o => this._optionHtml(o, selectedIds.has(o.id))).join("")
      prevGroup = true
    }

    if (filteredSpecs.length > 0) {
      html += `<div class="px-3 py-1.5 text-[11px] font-bold text-gray-400 uppercase tracking-wider bg-gray-50 border-b border-gray-100 ${prevGroup ? "border-t" : ""} sticky top-0">Specifications</div>`
      html += filteredSpecs.map(o => this._optionHtml(o, selectedIds.has(o.id))).join("")
    }

    this.listTarget.innerHTML = html
  }

  _optionHtml(option, isSelected) {
    const check = isSelected
      ? `<svg class="w-4 h-4 text-brand shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>`
      : `<div class="w-4 h-4 shrink-0 rounded border border-gray-300"></div>`
    return `<div data-action="click->spec-drawings-select#selectOption"
                 data-id="${this._esc(option.id)}"
                 data-label="${this._esc(option.label)}"
                 class="flex items-center gap-2.5 px-3 py-2 text-[13px] cursor-pointer transition-colors hover:bg-gray-50 ${isSelected ? "bg-brand/5" : ""}">
      ${check}
      <span class="truncate ${isSelected ? "text-brand font-medium" : "text-gray-700"}">${this._esc(option.label)}</span>
    </div>`
  }

  // ── Private: Render chips ──────────────────────────────────────

  _renderChips() {
    if (!this.hasChipsTarget) return

    // Trigger-mode input (readonly, click-to-browse): show a count summary
    // in the field itself; the chips below carry the details.
    if (this.hasInputTarget && this.inputTarget.readOnly) {
      this.inputTarget.value = this._selected.length > 0 ? `${this._selected.length} selected` : ""
    }

    if (this._selected.length === 0) {
      this.chipsTarget.innerHTML = ""
      this.chipsTarget.classList.add("hidden")
      return
    }

    this.chipsTarget.classList.remove("hidden")
    this.chipsTarget.innerHTML = this._selected.map(s => {
      const displayLabel = s.label.length > 40 ? s.label.substring(0, 37) + "..." : s.label
      return `<span class="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-brand/10 text-brand text-[13px] max-w-full">
        <span class="truncate">${this._esc(displayLabel)}</span>
        <button type="button" data-action="click->spec-drawings-select#removeChip" data-id="${this._esc(s.id)}"
                class="shrink-0 p-0.5 rounded-full hover:bg-brand/20 cursor-pointer">
          <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
      </span>`
    }).join("")
  }

  // ── Private: Sync hidden field ─────────────────────────────────
  // Combines chip selections + any free text in the input

  _syncHidden() {
    if (!this.hasHiddenTarget) return
    const parts = this._selected.map(s => s.id)
    // Trigger-mode input is readonly and shows "N selected" — never treat
    // that summary as typed free text. (Editable-combobox mode still saves
    // whatever was typed without a pick.)
    const freeText = (this.hasInputTarget && !this.inputTarget.readOnly) ? this.inputTarget.value.trim() : ""
    if (freeText) parts.push(freeText)
    this.hiddenTarget.value = parts.join(", ")
  }

  // Safe in BOTH text and attribute position — quotes must be escaped too,
  // or a value carrying an inch mark (`6" VERTICAL CURB`) closes its own
  // attribute and swallows the rest of the tag, data-* attributes included.
  _esc(str) {
    const d = document.createElement("div")
    d.textContent = str || ""
    return d.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }
}
